import { useState, useEffect, useCallback } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    AlertCircle,
    FileDown,
    Printer,
    Loader2,
    FileText,
    Tag,
    Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import toast from "react-hot-toast";
import { CompanyDocumentServices } from "@/services/CompanyDocument/CompanyDocumentServices";
import type {
    ICompanyDocument,
    NestedTabsProps,
    TemplateItem,
} from "@/interface/companyDocument.interface";
import {
    copyToClipboard,
    downloadPDF,
    getIconForDocument,
    getMyToken,
    mapCategoryToType,
    showToastNotification,
} from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import PageLoader from "@/components/common/PageLoader";
import CommonModal from "@/components/common/CommonModal";
import {
    DocumentPreview,
    type TDocumentInput,
} from "@/utility/documentPreview";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import EmployeeOnBoardService from "@/services/EmployeeOnBoard/EmployeeOnBoardService";

/**
 * Senior UI/UX Formatter for Document Body Content.
 * Formats plain text or HTML content into an executive, high-contrast typography hierarchy.
 */
const formatDocumentContent = (content: string): string => {
    if (!content) return "";

    // If content already contains HTML tags (<p>, <div>, <h1>, <br>, etc.), return directly
    if (/<[a-z][\s\S]*>/i.test(content)) {
        return content;
    }

    // Split plain text into lines
    const lines = content
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    return lines
        .map((line, idx) => {
            // Numbered heading pattern: "1. Introduction", "2. About the Trust", etc.
            const isNumberedHeading = /^\d+\.\s+/.test(line);
            // Metadata key-value pattern: "Effective Date: [Insert Date]"
            const isHeaderKey = /^(effective date|effective|date|version|author):/i.test(line);
            // Main title at top
            const isMainTitle = idx === 0 && line.length < 60;

            if (isNumberedHeading) {
                return `<h3 class="text-base font-bold text-slate-900 mt-8 mb-3.5 border-b border-slate-100 pb-2 flex items-center gap-2.5 tracking-tight">
                    <span class="inline-block w-2 h-2 rounded-full bg-primary shrink-0"></span>
                    <span>${line}</span>
                </h3>`;
            }

            if (isHeaderKey) {
                return `<div class="my-2.5 inline-flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200/80 px-3.5 py-1.5 text-xs font-semibold text-slate-600 shadow-2xs">
                    <span>${line}</span>
                </div>`;
            }

            if (isMainTitle) {
                return `<h2 class="text-lg font-extrabold text-slate-900 mt-1 mb-6 text-center tracking-tight uppercase">
                    ${line}
                </h2>`;
            }

            return `<p class="text-sm text-slate-600 leading-relaxed mb-4 tracking-normal">
                ${line}
            </p>`;
        })
        .join("");
};

export default function NestedTabs({ category }: NestedTabsProps) {
    const { userId } = useCurrentUser();
    const [templates, setTemplates] = useState<TemplateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [downloading, setDownloading] = useState(false);

    // Document Generation & Preview states
    const [generatingId, setGeneratingId] = useState<string | null>(null);
    const [selectedDoc, setSelectedDoc] = useState<TDocumentInput>(null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);

    const fetchDocuments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const type = mapCategoryToType(category);
            const response = await CompanyDocumentServices.getCompanyDocuments(
                `type=${type}&isActive=true`,
            );
            const docs = response?.data?.documents || [];

            const mapped: TemplateItem[] = docs?.map((doc: ICompanyDocument) => {
                const body = doc.content || "";
                const status = doc.isActive ? "Active" : "Inactive";
                const lastUpdated =
                    doc.updatedAt ||
                    doc.createdAt ||
                    new Date().toISOString().split("T")[0];

                return {
                    id: doc.id,
                    documentKey: doc.documentKey || doc.title,
                    value: doc.id,
                    label: doc.title,
                    icon: getIconForDocument(doc.title, category),
                    title: doc.title,
                    status,
                    lastUpdated: lastUpdated.slice(0, 10),
                    description: doc.description || "",
                    subject: doc.title,
                    body,
                    version: doc.version,
                    isActive: doc.isActive,
                };
            });

            setTemplates(mapped);
        } catch (err) {
            ThrowError(err);
            setError("Failed to load templates");
        } finally {
            setLoading(false);
        }
    }, [category]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    const handleDownloadPDF = async (tab: TemplateItem) => {
        try {
            setDownloading(true);
            const docKey = tab.documentKey || tab.title;

            const params = new URLSearchParams({
                documentKey: docKey,
                status: "current",
            });
            if (userId) params.append("userId", userId);

            const url = `${API_URL}${API_ENDPOINT?.report}/company-document/download?${params.toString()}`;
            const filename = `${tab.title.replace(/\s+/g, "_")}.pdf`;
            await downloadPDF(url, filename);
            showToastNotification("success", `PDF downloaded successfully!`);
        } catch (err) {
            ThrowError(err);
        } finally {
            setDownloading(false);
        }
    };

    const handlePrint = (tab: TemplateItem) => {
        const content = tab.body;
        const title = tab.subject || tab.title;

        const printWindow = window.open("", "_blank", "width=800,height=600");
        if (printWindow) {
            printWindow.document.write(`
          <html>
            <head>
              <title>${title}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 40px; line-height: 1.6; }
                h1, h2, h3 {     margin: 1.5em 0 0 0;
    padding: 0;
    border: 0; }
                ul, ol { padding-left: 1em; list-style: none; }
                li { margin-bottom: 0em; }
              </style>
            </head>
            <body>  
              <h1>${title}</h1>
              ${content}
            </body>
          </html>
        `);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
        } else {
            toast.error("Please allow popups to print.");
        }
    };

    const handleGenerateLetter = async (tab: TemplateItem) => {
        try {
            setGeneratingId(tab.id);
            const docKey = tab.documentKey || tab.title;
            const isOfferLetter =
                docKey.toLowerCase().includes("offer") ||
                tab.title.toLowerCase().includes("offer");

            if (isOfferLetter) {
                if (!userId) {
                    showToastNotification("danger", "User ID is missing.");
                    return;
                }
                const arrayBuffer =
                    await EmployeeOnBoardService.generateOfferLetter(userId);
                if (!arrayBuffer) {
                    showToastNotification("danger", "Failed to generate offer letter.");
                    return;
                }
                const blob = new Blob([arrayBuffer], { type: "application/pdf" });
                const file = new File([blob], "offer-letter.pdf", {
                    type: "application/pdf",
                });

                setSelectedDoc(file);
                setShowPreviewModal(true);
                showToastNotification("success", "Offer letter generated successfully!");
            } else {
                const params = new URLSearchParams({
                    documentKey: docKey,
                    status: "current",
                });
                if (userId) params.append("userId", userId);

                const url = `${API_URL}${API_ENDPOINT?.report}/company-document/download?${params.toString()}`;

                const token = getMyToken();
                const response = await fetch(url, {
                    method: "GET",
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });

                if (!response.ok) {
                    throw new Error("Failed to generate document");
                }

                const blob = await response.blob();
                const pdfBlob = new Blob([blob], { type: "application/pdf" });
                const file = new File([pdfBlob], `${docKey}.pdf`, {
                    type: "application/pdf",
                });

                setSelectedDoc(file);
                setShowPreviewModal(true);
                showToastNotification("success", "Document generated successfully!");
            }
        } catch (err) {
            ThrowError(err);
        } finally {
            setGeneratingId(null);
        }
    };

    const handleClosePreview = () => {
        setShowPreviewModal(false);
        setSelectedDoc(null);
    };

    if (loading) {
        return <PageLoader />;
    }

    if (error) {
        return (
            <div className="text-center py-10 border border-dashed rounded-xl bg-card">
                <AlertCircle className="mx-auto h-8 w-8 text-destructive mb-2" />
                <p className="text-sm text-destructive">{error}</p>
                <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={fetchDocuments}
                >
                    Retry
                </Button>
            </div>
        );
    }

    if (templates?.length === 0) {
        return (
            <div className="text-center py-10 border border-dashed rounded-xl bg-card">
                <span className="text-sm text-muted-foreground">
                    No active templates configured for {category}.
                </span>
            </div>
        );
    }

    const defaultValue = templates[0]?.id || "";
    const isLetterCategory = category === "letter";

    return (
        <>
            <Tabs
                defaultValue={defaultValue}
                orientation="vertical"
                className="flex flex-col items-start lg:flex-row gap-6"
            >
                <TabsList className="flex h-fit w-auto lg:w-64 flex-row lg:flex-col items-stretch justify-start rounded-xl border border-border/60 bg-card p-1.5 shadow-sm overflow-x-auto gap-0.5 scroll-bar-hidden">
                    {templates?.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.id}
                                className="group flex items-center justify-start gap-2.5 px-3.5 py-2.5 text-xs font-semibold rounded-lg bg-transparent text-muted-foreground border-0 hover:text-foreground data-[state=active]:bg-primary/10 data-[state=active]:text-primary transition-all duration-300 cursor-pointer min-w-fit flex-1 lg:flex-none text-left"
                            >
                                <Icon className="w-4 h-4 text-muted-foreground group-data-[state=active]:text-primary group-hover:scale-110 transition-transform duration-300" />
                                <span>{tab.label}</span>
                            </TabsTrigger>
                        );
                    })}
                </TabsList>

                <div className="flex-1 rounded-xl border border-border/60 bg-card p-5 sm:p-6 shadow-sm overflow-hidden">
                    {templates?.map((tab) => {
                        const isGeneratingThis = generatingId === tab.id;
                        return (
                            <TabsContent
                                key={tab.id}
                                value={tab.id}
                                className="mt-0 focus-visible:outline-none h-full space-y-6"
                            >
                                {/* Header with status badge */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-border/40">
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="text-lg font-bold text-foreground">
                                                {tab?.title}
                                            </h3>
                                            <Badge
                                                className={`text-[10px] py-0 px-2 rounded-full font-medium border ${tab.isActive
                                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                    : "bg-gray-100 text-gray-600 border-gray-200"
                                                    }`}
                                            >
                                                {tab.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {tab.description}
                                        </p>
                                    </div>
                                    <div className="text-right text-[10px] text-muted-foreground shrink-0">
                                        <span>Last Updated: {tab.lastUpdated}</span>
                                    </div>
                                </div>

                                <div className="space-y-5">
                                    {/* Enhanced UI for Document Subject / Header Template */}
                                    <div className="relative overflow-hidden rounded-xl border border-slate-200/80 bg-gradient-to-r from-slate-50/90 via-white to-primary/5 p-4 shadow-xs">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0 border border-primary/15">
                                                    <Tag className="w-4.5 h-4.5" />
                                                </div>
                                                <div>
                                                    <span className="text-[11px] font-bold tracking-wider text-slate-400 uppercase block">
                                                        Subject / Header Template
                                                    </span>
                                                    <h4 className="text-sm font-bold text-slate-800 tracking-tight mt-0.5">
                                                        {tab.subject || tab.title}
                                                    </h4>
                                                </div>
                                            </div>

                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    copyToClipboard(tab.subject || tab.title);
                                                    showToastNotification("success", "Subject copied to clipboard!");
                                                }}
                                                className="h-8 px-2.5 text-xs text-slate-600 hover:text-primary hover:bg-primary/5 hover:border-primary/30 rounded-lg transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
                                                title="Copy subject text"
                                            >
                                                <Copy className="w-3.5 h-3.5 text-slate-400" />
                                                <span className="hidden sm:inline font-medium">Copy</span>
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Hide Document Body Content if category is 'letter', show Generate box instead */}
                                    {isLetterCategory ? (
                                        <div className="rounded-xl border border-dashed border-border p-8 flex flex-col items-center justify-center text-center bg-slate-50/50 space-y-4 my-6">
                                            <div className="p-3 bg-primary/10 rounded-full text-primary">
                                                <FileText className="w-8 h-8" />
                                            </div>
                                            <div className="space-y-1 max-w-md">
                                                <h4 className="font-bold text-slate-800 text-sm">
                                                    Download {tab.title || "Letter"}
                                                </h4>
                                                <p className="text-xs text-muted-foreground">
                                                    Click the button below to generate and preview the document report.
                                                </p>
                                            </div>
                                            <Button
                                                onClick={() => handleGenerateLetter(tab)}
                                                disabled={isGeneratingThis}
                                                className="gap-2 bg-primary hover:bg-primary/95 text-white font-medium shadow-sm cursor-pointer"
                                            >
                                                {isGeneratingThis ? (
                                                    <>
                                                        <Loader2 className="w-4 h-4 animate-spin" />
                                                        Downloading Document...
                                                    </>
                                                ) : (
                                                    `Download ${tab?.title || "Letter"}`
                                                )}

                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="space-y-2.5">
                                            <div className="flex items-center justify-between px-1">
                                                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                                    Document Body Content
                                                </label>
                                                <span className="text-[11px] font-medium text-slate-400">
                                                    Executive Paper Layout
                                                </span>
                                            </div>

                                            {/* Senior UI/UX Paper Sheet Container */}
                                            <div className="relative rounded-2xl border border-slate-200/90 bg-white shadow-md shadow-slate-100/70 overflow-hidden">
                                                {/* Top Document Accent Header */}
                                                <div className="bg-slate-50/80 border-b border-slate-100 px-6 py-3 flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse"></span>
                                                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                                                            Official Document Content
                                                        </span>
                                                    </div>
                                                    <span className="text-[11px] font-semibold text-slate-400">
                                                        A4 Layout
                                                    </span>
                                                </div>

                                                {/* Document Body View with senior typography hierarchy */}
                                                <div className="p-8 sm:p-10 md:p-12 max-h-[580px] overflow-y-auto scroll-bar-hidden bg-white">
                                                    <div
                                                        className="prose prose-slate prose-sm sm:prose-base max-w-none text-slate-700 leading-relaxed font-sans select-text
                                                        [&_h1]:text-xl [&_h1]:font-extrabold [&_h1]:text-slate-900 [&_h1]:mt-8 [&_h1]:mb-4 [&_h1]:tracking-tight
                                                        [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-7 [&_h2]:mb-3.5 [&_h2]:border-b [&_h2]:border-slate-100 [&_h2]:pb-2
                                                        [&_h3]:text-base [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-7 [&_h3]:mb-3 [&_h3]:border-b [&_h3]:border-slate-100 [&_h3]:pb-1.5
                                                        [&_p]:text-slate-600 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm
                                                        [&_ul]:my-4 [&_ul]:pl-5 [&_ol]:my-4 [&_ol]:pl-5 [&_li]:my-1.5 [&_li]:text-slate-600 [&_li]:text-sm"
                                                        dangerouslySetInnerHTML={{ __html: formatDocumentContent(tab.body) }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center justify-end gap-3 border-t border-border/40 pt-4 mt-6">
                                    <div className="flex items-center gap-2">
                                        {!isLetterCategory && (
                                            <>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="gap-1.5 text-xs cursor-pointer border-primary/20 text-primary hover:bg-primary/5"
                                                    onClick={() => handleDownloadPDF(tab)}
                                                    disabled={downloading}
                                                >
                                                    <FileDown className="w-3.5 h-3.5" />
                                                    {downloading ? "Downloading..." : "Download PDF"}
                                                </Button>

                                                <Button
                                                    size="sm"
                                                    className="gap-1.5 text-xs bg-primary hover:bg-primary/95 text-white cursor-pointer"
                                                    onClick={() => handlePrint(tab)}
                                                >
                                                    <Printer className="w-3.5 h-3.5" />
                                                    Print Template
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </TabsContent>
                        );
                    })}
                </div>
            </Tabs>

            {/* Document Preview Modal */}
            <CommonModal
                title="Document Preview"
                show={showPreviewModal}
                onClose={handleClosePreview}
                className="sm:max-w-4xl"
            >
                <DocumentPreview selectedDocument={selectedDoc} />
            </CommonModal>
        </>
    );
}